import { Box, Flex } from '@chakra-ui/react';
import { ReactNode, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { AlphaBanner } from './AlphaBanner';
import { MobileTabBar } from './MobileTabBar';
import { AppSidebar } from './navigation/AppSidebar';
import { PageSubheader } from './navigation/PageSubheader';
import { useSidebarState } from './navigation/useSidebarState';
import { useWorkspaceNav } from './navigation/useWorkspaceNav';
import { CONTENT_PX, MOBILE_NAV_CLEARANCE, MOBILE_SCROLL_PB } from './layoutTokens';
import { UserProfile } from '@/client';
import { usePageChromeContent } from '@/contexts';
import { routePatterns } from '@/routes/paths';

interface AuthenticatedLayoutProps {
    children: ReactNode;
    user: UserProfile;
}

const MOBILE_SAVE_BAR_ROUTES = [
    routePatterns.homeExpenseNew,
    routePatterns.homeExpenseEdit,
    routePatterns.homeDocumentNew,
    routePatterns.homeDocumentEdit,
    routePatterns.homeAssetNew,
    routePatterns.homeAssetEdit,
    routePatterns.groupListingNew,
    routePatterns.groupListingEdit,
] as const;

/**
 * App shell.
 */
export function AuthenticatedLayout({ children, user }: AuthenticatedLayoutProps) {
    const { pathname } = useLocation();
    const nav = useWorkspaceNav();
    const chrome = usePageChromeContent();
    const sidebar = useSidebarState();

    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // jsdom does not implement Element.scrollTo.
        scrollRef.current?.scrollTo?.({ top: 0 });
    }, [pathname]);

    const activeSection = nav.sections.find((section) => section.id === nav.activeSectionId);
    const showSubheader = !activeSection?.noSubheader;
    const replaceMobileNavigation = MOBILE_SAVE_BAR_ROUTES.some((pattern) =>
        matchPath({ path: pattern, end: true }, pathname),
    );

    const content = (
        <>
            {showSubheader && (
                <PageSubheader
                    key={pathname}
                    nav={nav}
                    chrome={chrome}
                    compact={sidebar.isMobile}
                />
            )}

            <Box
                flex={1}
                minW={0}
                px={CONTENT_PX}
                pt={{ base: 3.5, md: 5 }}
                pb={sidebar.isMobile && !replaceMobileNavigation ? MOBILE_SCROLL_PB : 8}
            >
                {children}
            </Box>
        </>
    );

    if (sidebar.isMobile) {
        return (
            <Flex direction="column" h="100dvh" overflow="hidden" bg="bg.canvas">
                <AlphaBanner />

                <Flex
                    as="main"
                    ref={scrollRef}
                    direction="column"
                    flex={1}
                    minW={0}
                    minH={0}
                    overflowY="auto"
                    overscrollBehaviorY="contain"
                    css={
                        replaceMobileNavigation
                            ? undefined
                            : { '--mobile-nav-clearance': MOBILE_NAV_CLEARANCE }
                    }
                >
                    {content}
                </Flex>

                {!replaceMobileNavigation && <MobileTabBar nav={nav} user={user} />}
            </Flex>
        );
    }

    return (
        <Flex direction="column" h="100dvh" overflow="hidden" bg="bg.canvas">
            <AlphaBanner />

            <Flex flex={1} minH={0}>
                <AppSidebar
                    nav={nav}
                    user={user}
                    collapsed={sidebar.collapsed}
                    onToggleCollapsed={sidebar.toggleCollapsed}
                />

                <Flex as="main" ref={scrollRef} direction="column" flex={1} minW={0} overflowY="auto">
                    {content}
                </Flex>
            </Flex>
        </Flex>
    );
}
